import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "@/lib/api";
import { cachedApi, invalidateCache } from "@/lib/cachedApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Target, Sparkles, AlertTriangle,
  CheckSquare, ClipboardList, CheckCircle2, Circle, ChevronRight, RefreshCw, Receipt,
  Calculator, Download, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import InsightsWidget from "@/components/InsightsWidget";
import WeakDayBanner from "@/components/WeakDayBanner";
import { ReadinessSummary } from "@/components/readiness/ReadinessStages";
import TimeClockWidget from "@/components/TimeClockWidget";

const pctBr = (n, dec = 1, sign = false) =>
  n != null
    ? (sign && n > 0 ? "+" : "") +
      Number(n).toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec }) + "%"
    : "—";

const brlRound = (n) =>
  Math.round(n ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL",
    minimumFractionDigits: 0, maximumFractionDigits: 0 });

// Implantação — resumo da lista única (a mesma do painel da OPLYX). Some quando
// a loja está pronta; a lista completa fica em /implantacao.
function ReadinessCard({ r, onDismiss }) {
  const items = (r.stages || []).flatMap(st => st.items);
  const next = r.blockers.map(b => items.find(i => i.key === b.key)).filter(Boolean).slice(0, 3);
  return (
    <Card className="border-neutral-200/70 dark:border-white/[0.05] bg-white dark:bg-[#121417]" data-testid="onboarding-checklist">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 font-semibold">
            <CheckSquare className="w-4 h-4" strokeWidth={1.5} /> Implantação
          </div>
          <button onClick={onDismiss} className="text-xs text-muted-foreground hover:underline">Esconder por hoje</button>
        </div>
        <ReadinessSummary r={r} compact />
        {next.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Próximos passos</div>
            {next.map(it => (
              <Link key={it.key} to={it.who === "oplyx" ? "/implantacao" : (it.to || "/implantacao")}
                    className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-muted transition-colors">
                <Circle className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                <span className="flex-1 min-w-0 truncate">{it.label}</span>
                {it.who === "oplyx" && <span className="text-[10px] text-muted-foreground">a OPLYX cuida</span>}
                <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" strokeWidth={1.5} />
              </Link>
            ))}
          </div>
        )}
        <Link to="/implantacao" className="inline-flex items-center gap-0.5 text-xs font-medium hover:underline">
          Ver a implantação completa <ChevronRight className="w-3 h-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

function GoalRow({ label, achieved, target, pct, periodLabel, remaining, beyondGoal, byPerformance }) {
  const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 70 ? "bg-amber-500" : "bg-rose-500";
  const noTarget = !target || target === 0;
  return (
    <Card className="border-neutral-200/70 dark:border-white/[0.05] bg-white dark:bg-[#121417]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.75} />
            <span className="font-semibold text-sm">{label}</span>
            {periodLabel && <span className="text-xs text-neutral-400">{periodLabel}</span>}
          </div>
          <div className="tabular-nums text-sm">
            {noTarget
              ? <Link to="/goals" className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline">Definir meta →</Link>
              : <><span className="font-bold">{brlRound(achieved)}</span><span className="text-neutral-500"> / {brlRound(target)}</span></>
            }
          </div>
        </div>
        {beyondGoal && !noTarget && (
          <div className="mb-1.5 flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-400" data-testid="goal-row-beyond-goal">
            <Sparkles className="w-3 h-3 shrink-0" />
            Meta do mês batida. Sugestão extra pelo seu histórico, pra seguir ganhando margem
          </div>
        )}
        {!beyondGoal && byPerformance && !noTarget && (
          <div className="mb-1.5 text-[11px] text-neutral-500" data-testid="goal-row-by-performance">
            Calculada pelo seu faturamento real das últimas semanas
          </div>
        )}
        <div className="h-2 bg-neutral-100 dark:bg-white/[0.05] rounded-full overflow-hidden">
          <div className={`h-full ${noTarget ? "bg-neutral-300 dark:bg-white/[0.08]" : barColor} transition-all duration-500`} style={{ width: noTarget ? "0%" : `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between mt-1.5 text-xs">
          {noTarget
            ? <span className="text-neutral-400">Sem meta definida para este período</span>
            : <>
                <span className="text-neutral-500">{pctBr(pct)} concluída</span>
                {pct >= 100
                  ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Meta batida! Agora, foco em margem 🎯</span>
                  : remaining > 0 && <span className="text-neutral-500">faltam <b className="text-amber-700 dark:text-amber-400">{brl(remaining)}</b></span>
                }
              </>
          }
        </div>
      </CardContent>
    </Card>
  );
}

function GoalsSection({ d, weekRangeLabel, currentMonthLabel }) {
  const dg = d.daily_goal;
  const wg = d.week_goal;
  const mp = d.goal_progress;

  const todayLabel = new Date().toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });

  return (
    <div className="space-y-3" data-testid="goals-section">
      <GoalRow label="Meta do dia"
               achieved={dg?.achieved ?? 0} target={dg?.target ?? 0}
               pct={Math.min(100, dg?.percent ?? 0)}
               periodLabel={todayLabel}
               remaining={(dg?.percent ?? 0) < 100 ? (dg?.remaining ?? 0) : 0}
               beyondGoal={dg?.mode === "beyond_goal"}
               byPerformance={dg?.mode === "performance"} />
      <GoalRow label="Meta da semana"
               achieved={wg?.achieved ?? 0} target={wg?.target ?? 0}
               pct={Math.min(100, wg?.percent ?? 0)}
               periodLabel={weekRangeLabel}
               beyondGoal={wg?.mode === "beyond_goal"}
               byPerformance={wg?.mode === "performance"} />
      <GoalRow label="Meta do mês"
               achieved={mp?.achieved ?? 0} target={mp?.target ?? 0}
               pct={Math.min(100, mp?.percent ?? 0)}
               periodLabel={currentMonthLabel} />
    </div>
  );
}

function PayablesTodayCard({ data }) {
  const { count = 0, total = 0, items = [] } = data || {};

  if (count === 0) {
    return (
      <div className="rounded-xl border border-emerald-200/70 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/10 px-4 py-3 flex items-center gap-3" data-testid="payables-today-clear">
        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={1.5} />
        <span className="text-sm text-emerald-800 dark:text-emerald-300">Nenhuma conta vence hoje</span>
      </div>
    );
  }

  return (
    <Card className="border-amber-300/70 dark:border-amber-700/50 bg-amber-50/50 dark:bg-amber-950/20" data-testid="payables-today-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-amber-900 dark:text-amber-300 font-semibold text-sm">
            <Receipt className="w-4 h-4" strokeWidth={1.5} />
            Contas a pagar hoje
            <span className="text-xs font-normal text-amber-700/80 dark:text-amber-400/80">
              {count} parcela{count > 1 ? "s" : ""}
            </span>
          </div>
          <span className="tabular-nums font-bold text-amber-900 dark:text-amber-200">{brl(total)}</span>
        </div>
        <div className="space-y-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between text-sm text-amber-900/90 dark:text-amber-200/80">
              <span className="truncate min-w-0 mr-2">
                {item.description}
                {item.supplier_name && (
                  <span className="text-xs opacity-60 ml-1">· {item.supplier_name}</span>
                )}
              </span>
              <span className="tabular-nums shrink-0">{brl(item.amount)}</span>
            </div>
          ))}
          {count > 5 && (
            <div className="text-xs text-amber-700/70 dark:text-amber-400/70">
              + {count - 5} outra{count - 5 > 1 ? "s" : ""}…
            </div>
          )}
        </div>
        <Link to="/financial-central" className="text-xs text-amber-700 dark:text-amber-400 hover:underline" data-testid="payables-today-link">
          Ver todas as contas a pagar →
        </Link>
      </CardContent>
    </Card>
  );
}

const KPI = ({ label, value, hint, testid, live = false, highlight = false }) => (
  <div
    className={`kpi-card relative border rounded-xl p-4 transition-colors ${
      highlight
        ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-700/50"
        : "bg-white dark:bg-[#121417] border-neutral-200/70 dark:border-white/[0.05]"
    }`}
    data-testid={testid}
  >
    <div className="text-neutral-500 dark:text-neutral-500 text-[11px] font-medium uppercase tracking-[0.08em] mb-2.5 flex items-center gap-1.5">
      {live && (
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
      )}
      {label}
    </div>
    <div className={`text-xl md:text-2xl font-semibold tracking-tight tabular-nums ${
      highlight ? "text-emerald-700 dark:text-emerald-400" : ""
    }`}>{value}</div>
    {hint && <div className="text-[11px] text-neutral-500 dark:text-neutral-500 mt-1 leading-snug">{hint}</div>}
  </div>
);

const D_TTL = 2 * 60 * 1000;

const MONTHS_FULL = ["janeiro","fevereiro","março","abril","maio","junho",
  "julho","agosto","setembro","outubro","novembro","dezembro"];

function AccountingPackageBanner() {
  const [downloading, setDownloading] = useState(false);
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthLabel = `${MONTHS_FULL[prevMonth.getMonth()]}/${prevMonth.getFullYear()}`;

  const download = async () => {
    setDownloading(true);
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("oplyx_token");
    try {
      const res = await fetch(`${base}/api/accounting/monthly-package`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oplyx_contabilidade_${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pacote contábil exportado.");
    } catch (_) {
      toast.error("Erro ao gerar pacote contábil.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200/70 dark:border-white/[0.05] bg-white dark:bg-[#121417] p-4 flex items-center justify-between flex-wrap gap-2" data-testid="accounting-package-banner">
      <div className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
        <Calculator className="w-4 h-4 shrink-0" strokeWidth={1.5} />
        Pacote contábil de <b className="capitalize">{prevMonthLabel}</b> pronto para gerar
      </div>
      <Button size="sm" onClick={download} disabled={downloading}
              className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white"
              data-testid="accounting-package-banner-btn">
        {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
        Baixar
      </Button>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [intelReady, setIntelReady] = useState(false);

  useEffect(() => {
    cachedApi("oplyx_dash_v1", () => api.get("/dashboard"), D_TTL)
      .then(data => {
        setD(data);
        let hidden = false;
        try { hidden = sessionStorage.getItem("oplyx_readiness_hidden") === "1"; } catch { /* opcional */ }
        if (!hidden && (data._onboarding_pct ?? 100) < 100) {
          api.get("/onboarding/readiness").then(r2 => setOnboarding(r2.data)).catch(() => {});
        }
      })
      .catch(() => {});

    const bust = () => {
      invalidateCache("oplyx_dash_v1");
      api.get("/dashboard").then(r => setD(r.data)).catch(() => {});
    };
    window.addEventListener("logo-updated", bust);
    window.addEventListener("sale-completed", bust);
    return () => {
      window.removeEventListener("logo-updated", bust);
      window.removeEventListener("sale-completed", bust);
    };
  }, []);

  // Goals auto-refresh every 60s so progress bars stay live as sales come in
  useEffect(() => {
    const interval = setInterval(() => {
      api.get("/dashboard").then(r => setD(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Defer intelligence widget one tick after main KPIs
  useEffect(() => {
    if (!d) return;
    const t = setTimeout(() => setIntelReady(true), 50);
    return () => clearTimeout(t);
  }, [!!d]);

  // Dispensar esconde o aviso, que continua aberto até o saldo voltar acima do mínimo.
  const dismissAlerts = async () => {
    try {
      await api.put("/alerts/dismiss-all");
      setD(prev => prev ? { ...prev, _alerts: [] } : prev);
    } catch { toast.error("Não consegui dispensar. Tente de novo."); }
  };

  const user = JSON.parse(localStorage.getItem("oplyx_user") || "{}");
  const isStaff = user.role === "staff";
  const isOwner = user.role === "owner";

  if (!d) return <div className="text-muted-foreground text-sm p-4">Carregando...</div>;

  const _now = new Date();
  const _months = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  const currentMonthLabel = `${_months[_now.getMonth()]}/${_now.getFullYear()}`;
  const _dow = _now.getDay();
  const _mon = new Date(_now); _mon.setDate(_now.getDate() - (_dow === 0 ? 6 : _dow - 1));
  const _sun = new Date(_mon); _sun.setDate(_mon.getDate() + 6);
  const _fmt = (dt) => dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const weekRangeLabel = `${_fmt(_mon)} – ${_fmt(_sun)}`;
  const _h = _now.getHours();
  const greeting = _h < 12 ? "Bom dia" : _h < 18 ? "Boa tarde" : "Boa noite";
  const refreshDash = () => { invalidateCache("oplyx_dash_v1"); api.get("/dashboard").then(r => setD(r.data)).catch(() => {}); };

  const alerts = d._alerts || [];
  const logoUrl = d._logo_url || null;

  // Monthly card helpers
  const forecastPct = d.goal_progress?.target > 0
    ? Math.round((d.forecast_revenue ?? 0) / d.goal_progress.target * 100)
    : null;

  const docsUsed  = d.docs_used_month ?? 0;
  const docsLimit = d.docs_limit_month ?? 0;
  const docsPct   = docsLimit > 0 ? Math.min(100, Math.round(docsUsed / docsLimit * 100)) : null;

  return (
    <div className="space-y-5" data-testid="dashboard-page">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4 flex-wrap">
        {logoUrl && (
          <img src={logoUrl} alt="logo"
               className="w-12 h-12 md:w-14 md:h-14 object-contain rounded-xl border bg-white dark:bg-[#121417] dark:border-white/[0.05] p-1"
               data-testid="dashboard-logo" />
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="dashboard-hero-title">
            {greeting}, {user.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-500 mt-0.5" data-testid="dashboard-hero-meta">
            {_now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            {" · "}{user.business_name}
          </p>
        </div>
        <button onClick={refreshDash}
          className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-200 dark:border-white/[0.06] text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/[0.04] transition-colors"
          data-testid="dashboard-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} />
          Atualizar
        </button>
      </div>

      {/* ── Ponto eletrônico (colaboradores e gestores apenas) ──────────── */}
      {!isOwner && <TimeClockWidget />}

      {/* ── Meta do dia (colaboradores e gestores apenas) ────────────────── */}
      {!isOwner && (
        <GoalRow
          label="Meta do dia"
          achieved={d.daily_goal?.achieved ?? 0}
          target={d.daily_goal?.target ?? 0}
          pct={Math.min(100, d.daily_goal?.percent ?? 0)}
          periodLabel={new Date().toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}
          remaining={(d.daily_goal?.percent ?? 0) < 100 ? (d.daily_goal?.remaining ?? 0) : 0}
        />
      )}

      {/* ── Banners urgentes ─────────────────────────────────────────────── */}
      {!isStaff && _now.getDate() === 1 && <AccountingPackageBanner />}

      {d.my_pending_tasks > 0 && (
        <div className="rounded-xl border border-amber-300/70 dark:border-amber-700/50 bg-amber-50/80 dark:bg-amber-950/20 p-4 space-y-2" data-testid="my-tasks-banner">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-300 font-semibold text-sm">
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} />
              Você tem {d.my_pending_tasks} tarefa{d.my_pending_tasks > 1 ? "s" : ""} pendente{d.my_pending_tasks > 1 ? "s" : ""} delegada{d.my_pending_tasks > 1 ? "s" : ""} pelo gestor
            </div>
            <Link to="/tasks" data-testid="my-tasks-banner-cta">
              <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">Ver minhas tarefas</Button>
            </Link>
          </div>
          {(d.my_tasks_preview || []).slice(0, 3).map(t => (
            <div key={t.id} className="flex items-center justify-between text-sm text-amber-900/90 dark:text-amber-200/90 pl-6" data-testid={`my-task-row-${t.id}`}>
              <span className="truncate">
                <b>{t.title}</b>
                {t.due_date && <span className="text-xs opacity-70 ml-2">· até {new Date(t.due_date).toLocaleDateString("pt-BR")}</span>}
                <span className="text-xs opacity-70 ml-2">· por {t.created_by_name}</span>
              </span>
              {t.priority === "high" && <span className="text-[10px] font-semibold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded uppercase tracking-wider ml-2">Alta</span>}
            </div>
          ))}
          {d.my_pending_tasks > 3 && (
            <div className="text-xs text-amber-800/70 dark:text-amber-300/70 pl-6">+ {d.my_pending_tasks - 3} outra{d.my_pending_tasks - 3 > 1 ? "s" : ""} tarefa{d.my_pending_tasks - 3 > 1 ? "s" : ""}…</div>
          )}
        </div>
      )}

      {alerts.length > 0 && (() => {
        const negativos = alerts.filter(a => Number(a.stock) < 0);
        const baixos = alerts.filter(a => !(Number(a.stock) < 0));
        const nomes = (l) => l.slice(0, 2).map(a => a.product_name).join(", ") + (l.length > 2 ? ` e mais ${l.length - 2}` : "");
        return (
          <div className="rounded-xl border border-rose-200/70 dark:border-rose-900/40 bg-rose-50/50 dark:bg-rose-950/20 p-4" data-testid="alerts-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1 min-w-0 text-sm text-rose-900 dark:text-rose-200">
                <div className="flex items-center gap-2 font-semibold text-rose-900 dark:text-rose-300">
                  <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.5} />Estoque pede atenção
                </div>
                {baixos.length > 0 && (
                  <div data-testid="alerts-low">{baixos.length} {baixos.length > 1 ? "itens abaixo do mínimo" : "item abaixo do mínimo"}: {nomes(baixos)}.</div>
                )}
                {negativos.length > 0 && (
                  <div data-testid="alerts-negative">{negativos.length} {negativos.length > 1 ? "itens com saldo negativo" : "item com saldo negativo"} ({nomes(negativos)}). Vale fazer uma contagem.</div>
                )}
                <Link to="/estoque" className="inline-block underline underline-offset-2 text-rose-800 dark:text-rose-300" data-testid="alerts-open-stock">Ver estoque</Link>
              </div>
              <Button size="sm" variant="ghost" className="h-7 shrink-0 text-xs dark:hover:bg-white/[0.04]" onClick={dismissAlerts} data-testid="alerts-dismiss">
                Dispensar
              </Button>
            </div>
          </div>
        );
      })()}

      {/* ── Row 1 — operacional de hoje (4 cards) ────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-row-today">
        <KPI testid="kpi-today" label="Faturamento do dia" value={brl(d.today_revenue)} live highlight
             hint={d.today_vs_yesterday_pct != null
               ? `${pctBr(d.today_vs_yesterday_pct, 1, true)} vs. ontem`
               : undefined} />
        <KPI testid="kpi-orders" label="Pedidos do dia"
             value={d.today_sales_count ?? 0}
             hint={(d.open_tabs_count ?? 0) > 0 ? `${d.open_tabs_count} em aberto agora` : "sem abertos no momento"} />
        <KPI testid="kpi-avg-ticket" label="Ticket médio do dia"
             value={brl(d.today_avg_ticket ?? 0)}
             hint={(d.today_sales_count ?? 0) > 0 ? `${d.today_sales_count} atendimento(s)` : undefined} />
        <KPI testid="kpi-cmv-today" label="CMV ponderado do dia"
             value={pctBr(d.today_cmv_pct)}
             hint={d.today_cmv_val != null ? brl(d.today_cmv_val) + " em insumos" : undefined} />
      </div>

      {/* ── Row 2 — análise mensal (4 cards) ─────────────────────────────── */}
      {!isStaff && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-row-month">
          <KPI testid="kpi-forecast" label="Previsão do mês"
               value={brl(d.forecast_revenue ?? 0)}
               hint={[
                 "média diária × dias restantes",
                 forecastPct != null ? `${pctBr(forecastPct, 0)} da meta` : null,
               ].filter(Boolean).join(" · ")} />

          <KPI testid="kpi-real-profit" label="Lucro líquido do mês"
               value={brl(d.real_profit ?? 0)}
               hint={d.net_profit_forecast != null
                 ? `projeção: ${brl(d.net_profit_forecast)}`
                 : `margem: ${pctBr(d.profit_margin)}`} />

          <KPI testid="kpi-cmv-month" label="CMV ponderado mês"
               value={pctBr(d.cmv_weighted_pct)}
               hint={[
                 "ponderado vendas",
                 d.cmv_products_avg_pct != null
                   ? `médio fichas ${pctBr(d.cmv_products_avg_pct)}${d.cmv_products_tracked != null ? ` (${d.cmv_products_tracked})` : ""}`
                   : null,
               ].filter(Boolean).join(" · ")} />

          <KPI testid="kpi-docs-fiscal" label="Docs fiscais do mês"
               value={docsLimit > 0
                 ? `${docsUsed.toLocaleString("pt-BR")} / ${docsLimit.toLocaleString("pt-BR")}`
                 : docsUsed.toLocaleString("pt-BR")}
               hint={docsPct != null
                 ? `${pctBr(docsPct, 0)} utilizado · ${currentMonthLabel}`
                 : currentMonthLabel} />
        </div>
      )}

      {/* ── Contas a pagar do dia ────────────────────────────────────────── */}
      {!isStaff && (
        <PayablesTodayCard data={d.payables_today} />
      )}

      {/* ── Alerta de dia fraco (Intelligence › Hoje) ────────────────────── */}
      {!isStaff && <WeakDayBanner />}

      {/* ── Metas — dia / semana / mês ───────────────────────────────────── */}
      {!isStaff && (
        <GoalsSection d={d} weekRangeLabel={weekRangeLabel} currentMonthLabel={currentMonthLabel} />
      )}

      {/* ── Oplyx Intelligence ───────────────────────────────────────────── */}
      {!isStaff && intelReady && (
        <div className="rounded-xl border border-neutral-200/70 dark:border-white/[0.05] bg-white dark:bg-[#121417]" data-testid="intelligence-section">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-neutral-200/70 dark:border-white/[0.05]">
            <Sparkles className="w-4 h-4 text-[#00A855] dark:text-[#00FF88]" strokeWidth={1.5} />
            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Oplyx Intelligence</span>
          </div>
          <div className="px-5 pb-5 pt-4">
            <InsightsWidget />
          </div>
        </div>
      )}

      {/* ── Onboarding (último — não é urgente) ──────────────────────────── */}
      {!isStaff && onboarding && onboarding.state !== "operando" && onboarding.state !== "pronto" && (
        <ReadinessCard r={onboarding} onDismiss={() => {
          try { sessionStorage.setItem("oplyx_readiness_hidden", "1"); } catch { /* opcional */ }
          setOnboarding(null);
        }} />
      )}
    </div>
  );
}
